import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import { DATE_FORMATS } from '@/helpers/dates/utils/const'
import { dateFormat } from '@/helpers/dates'
import runUserSettingsMigrations from './utils/helpers'

import { useInactivityHandler } from '@/plugins/inactivity'

import { useUserStore } from '.'

import api from '@/store/api'

export const CURRENT_TERMS_VERSION = '2026-04-30'

const US_DATE_FORMAT = Intl.DateTimeFormat()
  .resolvedOptions()
  .timeZone.includes('America/')

export const useUserSettingsStore = defineStore('user-settings', () => {
  // INIT
  const userStore = useUserStore()
  const inactivityHandler = useInactivityHandler()

  const data = ref<Record<string, any>>()

  const isInited = ref(false)

  const alreadyMigrated = ref(false)

  let migrationStarted = false

  // GETTERS
  const getDateFormat = computed(
    () => data.value?.dateFormat || getActualDateFormat(),
  )
  const getLastActiveRepository = computed(
    () => data.value?.lastActiveRepository,
  )
  const getAllowedInactivityTime = computed(
    () => data.value?.allowedInactivityTime,
  )
  const getEarlyAccess = computed(() => data.value?.earlyAccess)

  const getAssetTreeWidth = computed(() => data.value?.assetTreeWidth)

  const getAdvisorMode = computed(() => data.value?.advisorMode ?? false)

  const hasAcceptedCurrentTerms = computed(
    () => data.value?.termsOfServiceAcceptance?.version === CURRENT_TERMS_VERSION,
  )

  // SETTERS

  const setData = async (value: Record<string, any>) => {
    data.value = value
  }

  const setDateFormat = (dateFormat: string) => {
    update({ dateFormat })
  }

  const setLastActiveRepository = (lastActiveRepository?: string) => {
    update({ lastActiveRepository })
  }

  const setAllowedInactivityTime = (allowedInactivityTime?: number) => {
    if (!allowedInactivityTime) return
    update({ allowedInactivityTime })
  }

  const setAssetTreeWidth = (assetTreeWidth: number) => {
    update({ assetTreeWidth })
  }

  const setAdvisorMode = (advisorMode: boolean) => {
    update({ advisorMode })
  }

  const acceptCurrentTerms = () => update({
    termsOfServiceAcceptance: {
      version: CURRENT_TERMS_VERSION,
      acceptedAt: new Date().toISOString()
    },
  })

  // ACTIONS

  const getActualDateFormat = () =>
    US_DATE_FORMAT ? DATE_FORMATS[1] : DATE_FORMATS[0]

  const fetch = async () => {
    const allpositUuid = await userStore.getAndValidateUuid()
    if (!allpositUuid) return
    const result = await api.get(`users/${allpositUuid}/settings`)
    setData(result.data)
    isInited.value = true
  }

  const update = async (value: Record<string, any>) => {
    const allpositUuid = await userStore.getAndValidateUuid()
    if (!allpositUuid) return
    const params = {
      ...data.value,
      ...value,
    }
    const result = await api.post(`users/${allpositUuid}/settings`, params)
    data.value = result.data
    return result.data
  }

  const clear = () => {
    isInited.value = false
    data.value = undefined
    migrationStarted = false
    alreadyMigrated.value = false
  }

  watch(
    () => [isInited.value, getAllowedInactivityTime.value],
    ([inited, waitingTime]) => {
      if (!inited) return
      inactivityHandler.start(waitingTime)
    },
    { immediate: true },
  )

  watch(
    getDateFormat,
    value => {
      dateFormat.value = value
    },
    { immediate: true },
  )

  watch(data, async value => {
    if (!value || migrationStarted) return
    migrationStarted = true
    const newSettings = await runUserSettingsMigrations(value)
    if (newSettings) {
      await setData(newSettings)
      update({})
    }
    alreadyMigrated.value = true
  }, { immediate: true })

  return {
    isInited,
    alreadyMigrated,

    getDateFormat,
    getLastActiveRepository,
    getAllowedInactivityTime,
    getEarlyAccess,
    getAssetTreeWidth,
    getAdvisorMode,
    hasAcceptedCurrentTerms,

    setDateFormat,
    setLastActiveRepository,
    setAllowedInactivityTime,
    setAssetTreeWidth,
    setAdvisorMode,
    acceptCurrentTerms,

    fetch,

    clear,
  }
})
